import { useQuery } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { fetchClientLogo, type ClientLogoMetadata } from "../api/companyLogo";
import { authStore } from "../../lib/auth/authStore";
import { queryClient, SCOPED_PAGE_GC_MS } from "../../lib/queryClient";

const NEGATIVE_CACHE_MS = 15 * 60_000;
const PENDING_RETRY_MS = 2_000;
const MAX_PENDING_READS = 4;
const PENDING_COOLDOWN_MS = 60_000;

/** Nonblocking, actor/client-isolated metadata; validated image bytes use the existing HTTP cache. */
export function ClientLogo({ clientId, name, size = 18 }: {
  clientId: string;
  name: string;
  size?: number;
}): React.ReactElement {
  const actor = authStore.cacheIdentity;
  // Include the current name so a rename cannot retain the old company's logo on a warm visit.
  const queryKey = ["scoped-page", actor, "client-logo", clientId, name];
  const query = useQuery({
    queryKey,
    // Cache ownership intentionally outlives an individual sidebar row. As with scoped-page reads,
    // QueryClient.clear() cancels/discards late results when the authenticated identity changes.
    queryFn: () => fetchClientLogo(clientId),
    enabled: actor !== null && Boolean(clientId),
    retry: false,
    staleTime: (state) => state.state.data?.status === "available"
      ? SCOPED_PAGE_GC_MS
      : state.state.data?.status === "pending"
        ? state.state.dataUpdateCount < MAX_PENDING_READS ? PENDING_RETRY_MS : PENDING_COOLDOWN_MS
        : NEGATIVE_CACHE_MS,
    gcTime: SCOPED_PAGE_GC_MS,
    refetchOnWindowFocus: false,
    refetchInterval: (state) => state.state.status !== "error" && state.state.data?.status === "pending"
      && state.state.dataUpdateCount < MAX_PENDING_READS ? PENDING_RETRY_MS : false,
  }, queryClient);
  const logo = actor !== null && !query.isError && query.data?.status === "available"
    ? query.data.logo : null;

  const handleImageError = (): void => {
    // Cache broken assets briefly too. Do not recreate a protected entry after logout/eviction,
    // or replace a newer response when an older image finishes loading late.
    if (!logo || authStore.cacheIdentity !== actor || !queryClient.getQueryState(queryKey)) return;
    queryClient.setQueryData<ClientLogoMetadata>(queryKey, (current) => (
      current?.status === "available" && current.logo.asset_url === logo.asset_url
        ? { status: "unavailable", logo: null } : current
    ));
  };

  return (
    <span
      aria-hidden="true"
      data-client-logo={clientId}
      className="inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      {logo ? (
        <img
          src={logo.asset_url}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="h-full w-full object-contain"
          onError={handleImageError}
        />
      ) : <Building2 size={size} aria-hidden />}
    </span>
  );
}
